/**
 * PWWCapital — Centralized API Error Normalizer (Section 16)
 *
 * Converts raw Axios errors into a predictable shape that the UI
 * can consume safely, with no raw backend messages ever shown verbatim.
 */

/** @typedef {{ message: string; code: string; statusCode: number; retryable: boolean; fields: Record<string, string[]> }} NormalizedError */

const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

const STATUS_META = {
  400: { code: 'VALIDATION_ERROR', retryable: false },
  401: { code: 'UNAUTHORIZED',    retryable: false },
  403: { code: 'FORBIDDEN',       retryable: false },
  404: { code: 'NOT_FOUND',       retryable: false },
  408: { code: 'TIMEOUT',         retryable: true  },
  409: { code: 'CONFLICT',        retryable: false },
  422: { code: 'UNPROCESSABLE',   retryable: false },
  429: { code: 'RATE_LIMITED',    retryable: true  },
  500: { code: 'SERVER_ERROR',    retryable: true  },
  502: { code: 'BAD_GATEWAY',     retryable: true  },
  503: { code: 'UNAVAILABLE',     retryable: true  },
  504: { code: 'GATEWAY_TIMEOUT', retryable: true  },
};

/**
 * Converts any caught error into a NormalizedError object.
 * This is the ONLY place where backend error text is processed.
 *
 * @param {unknown} error
 * @returns {NormalizedError}
 */
export function normalizeApiError(error) {
  // Network failure (no response from server)
  if (!error?.response) {
    return {
      message: 'Unable to connect to the server. Please check your connection.',
      code: NETWORK_ERROR_CODE,
      statusCode: 0,
      retryable: true,
      fields: {},
    };
  }

  const { status, data } = error.response;

  // Field-level validation errors (DRF style: { field: ['message'] })
  const fields = {};
  if (data && typeof data === 'object') {
    for (const [key, val] of Object.entries(data)) {
      if (key !== 'detail' && key !== 'non_field_errors' && Array.isArray(val)) {
        fields[key] = val;
      }
    }
  }

  const friendlyMessages = {
    400: extractDetail(data) || 'The request contained invalid data. Please review your inputs.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource could not be found.',
    409: extractDetail(data) || 'A conflict occurred. This item may already exist.',
    422: extractDetail(data) || 'Unable to process your request due to validation errors.',
    429: 'Too many requests. Please wait a moment before trying again.',
    500: 'An unexpected server error occurred. Our team has been notified.',
    502: 'The service is temporarily unavailable. Please try again shortly.',
    503: 'The service is under maintenance. Please try again later.',
  };

  return {
    message: friendlyMessages[status] ?? `An unexpected error occurred (${status}).`,
    code: STATUS_META[status]?.code ?? String(status),
    statusCode: status,
    retryable: STATUS_META[status]?.retryable ?? false,
    fields,
  };
}

function extractDetail(data) {
  if (!data) return null;
  if (typeof data === 'string') return null; // never expose raw strings
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join(' ');
  return null;
}

/**
 * Returns a short user-facing field error string or null.
 * @param {NormalizedError} err
 * @param {string} field
 */
export function getFieldError(err, field) {
  const msgs = err?.fields?.[field];
  if (!msgs || !msgs.length) return null;
  return msgs[0];
}
